import { useCallback, useEffect, useState } from "react";
import type { VnProgram } from "../engine/Interpreter";
import type { SaveData, SaveMeta, SaveStorage } from "../engine/SaveLoad";
import type { AchievementSink } from "../engine/AchievementStore";
import { connectHeartbeat as openHeartbeatSocket } from "./HeartbeatClient";
import { setAssetRoot } from "../engine/assetRoot";
import type {
  AuthError,
  AuthResult,
  CatalogueEntry,
  GrantPayload,
  PlatformAdapter,
  Session,
  UrlParamDef,
} from "./PlatformAdapter";

// Mirrors server/src/arctree_server/routes_games.py's GameCard — only the
// fields this client actually reads are declared.
interface GameCard {
  id: string;
  status: "available" | "subscription_required" | "disabled_global" | "restricted_temporary";
  title: string;
  bundle_url?: string;
  assets_base_url?: string;
  url_params?: UrlParamDef[];
}

interface GameListResponse {
  games: GameCard[];
}

// Mirrors routes_saves.py's SaveRecord/SaveListResponse.
interface SaveRecord {
  slot_id: string;
  data: SaveData;
  save_schema_version: number;
  is_rollback: boolean;
}

interface SaveListResponse {
  saves: SaveRecord[];
}

const LOCK_REASONS: Record<Exclude<GameCard["status"], "available">, string> = {
  subscription_required: "Subscription required",
  disabled_global: "Unavailable",
  restricted_temporary: "Restricted",
};

export function toCatalogueEntry(g: GameCard): CatalogueEntry {
  if (g.status === "available") {
    return g.url_params
      ? { id: g.id, title: g.title, status: "available", urlParams: g.url_params }
      : { id: g.id, title: g.title, status: "available" };
  }
  return { id: g.id, title: g.title, status: "locked", lockReason: LOCK_REASONS[g.status] };
}

// Populated by every successful /api/games fetch so loadGame(id) can usually
// resolve a bundle_url without its own round-trip. Module scope is fine —
// platform/index.ts hands out a single ServerPlatform instance for the life
// of the tab.
let cachedGames = new Map<string, GameCard>();

async function fetchCatalogue(): Promise<GameCard[]> {
  const res = await fetch("/api/games", { credentials: "include" });
  // Unauthenticated (401) or unreachable — Module 12: play continues,
  // persistence/catalogue disabled, not a hard failure.
  if (!res.ok) return [];
  const data = (await res.json()) as GameListResponse;
  cachedGames = new Map(data.games.map((g) => [g.id, g]));
  return data.games;
}

// login/register/logout all mutate the session; catalogue and session state
// both depend on it, so both re-fetch whenever notifySessionChanged() fires
// rather than only once on mount. Without this, logging in from an already-
// mounted (anonymous) title screen would never reveal the catalogue — the
// initial anonymous fetch already ran and nothing would trigger a second one.
const sessionListeners = new Set<() => void>();
function notifySessionChanged(): void {
  sessionListeners.forEach((l) => l());
}

function useCatalogue(): CatalogueEntry[] {
  const [entries, setEntries] = useState<CatalogueEntry[]>([]);

  const refetch = useCallback(() => {
    fetchCatalogue()
      .then((games) => setEntries(games.map(toCatalogueEntry)))
      .catch(() => setEntries([]));
  }, []);

  useEffect(() => {
    refetch();
    sessionListeners.add(refetch);
    return () => {
      sessionListeners.delete(refetch);
    };
  }, [refetch]);

  return entries;
}

async function fetchPreludeCard(): Promise<GameCard | null> {
  try {
    const res = await fetch("/api/prelude", { credentials: "include" });
    if (!res.ok) return null; // 404 (not yet ingested) or unreachable — expected, not an error
    const card = (await res.json()) as GameCard;
    cachedGames.set(card.id, card); // so a subsequent loadGame(id) finds it without a second fetch
    return card;
  } catch {
    return null;
  }
}

async function loadGame(id: string): Promise<VnProgram> {
  // A direct/reloaded URL (?story=id) can call this before any component has
  // ever mounted useCatalogue() — cachedGames would otherwise be empty even
  // though the id is perfectly valid. Fetch once ourselves in that case
  // rather than assuming some other hook already primed the cache.
  let card = cachedGames.get(id);
  if (!card?.bundle_url) {
    const games = await fetchCatalogue();
    card = games.find((g) => g.id === id);
  }
  if (!card?.bundle_url) {
    // Prelude is reachable even fully logged out, via a separate endpoint the
    // authenticated catalogue fetch above never touches (req Module 12) — the
    // fallback above only ever finds it for an already-verified account.
    const prelude = await fetchPreludeCard();
    if (prelude?.id === id) card = prelude;
  }
  const bundleUrl = card?.bundle_url;
  if (!bundleUrl) {
    throw new Error(`No playable bundle for "${id}" (not in your catalogue, or the game is locked)`);
  }
  // Per-game asset root (design §3.4/§7.1). Until the server sends
  // assets_base_url (Phase C), an empty root preserves today's behavior:
  // game assets resolve against the client origin's baked-in tree.
  setAssetRoot(card?.assets_base_url ?? "");
  const res = await fetch(bundleUrl, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  return res.json() as Promise<VnProgram>;
}

async function fetchPrelude(): Promise<CatalogueEntry | null> {
  const card = await fetchPreludeCard();
  return card ? toCatalogueEntry(card) : null;
}

class ServerSaveStorage implements SaveStorage {
  constructor(private readonly gameId: string) {}

  private get _listUrl(): string {
    return `/api/saves/${this.gameId}`;
  }

  private _slotUrl(slotId: string): string {
    return `/api/saves/${this.gameId}/${slotId}`;
  }

  private async _fetchActiveSaves(): Promise<SaveRecord[]> {
    const res = await fetch(this._listUrl, { credentials: "include" });
    if (!res.ok) return [];
    const body = (await res.json()) as SaveListResponse;
    return body.saves.filter((s) => !s.is_rollback);
  }

  async get(key: string): Promise<SaveData | null> {
    const saves = await this._fetchActiveSaves();
    const rec = saves.find((s) => s.slot_id === key);
    return rec ? rec.data : null;
  }

  async set(key: string, data: SaveData): Promise<void> {
    const res = await fetch(this._slotUrl(key), {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data, save_schema_version: data.meta.schemaVersion }),
    });
    if (!res.ok) throw new Error(`Save failed: HTTP ${res.status}: ${res.statusText}`);
  }

  async delete(key: string): Promise<void> {
    const res = await fetch(this._slotUrl(key), { method: "DELETE", credentials: "include" });
    if (!res.ok && res.status !== 404) {
      throw new Error(`Delete save failed: HTTP ${res.status}: ${res.statusText}`);
    }
  }

  async list(): Promise<SaveMeta[]> {
    const saves = await this._fetchActiveSaves();
    return saves.map((s) => s.data.meta);
  }
}

function createSaveStorage(gameId: string): SaveStorage {
  return new ServerSaveStorage(gameId);
}

// Mirrors routes_achievements.py's UnlockResponse/AchievementListResponse.
interface UnlockResponse {
  achievement_id: string;
  unlocked: boolean;
}

interface AchievementListResponse {
  achievement_ids: string[];
}

class ServerAchievementSink implements AchievementSink {
  async loadUnlocked(gameId: string): Promise<Set<string>> {
    const res = await fetch(`/api/achievements?game=${encodeURIComponent(gameId)}`, { credentials: "include" });
    if (!res.ok) return new Set(); // unauthenticated/unreachable — no persisted unlocks to report
    const body = (await res.json()) as AchievementListResponse;
    return new Set(body.achievement_ids);
  }

  async markUnlocked(gameId: string, achievementId: string): Promise<void> {
    const res = await fetch("/api/achievements/unlock", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ game_id: gameId, achievement_id: achievementId }),
    });
    if (!res.ok) return; // best-effort — AchievementEngine already re-fire-guards locally
    (await res.json()) as UnlockResponse;
  }
}

const serverAchievementSink = new ServerAchievementSink();
function achievementSink(): AchievementSink {
  return serverAchievementSink;
}

// Mirrors routes_grants.py's GrantPayload response model.
interface GrantResponse {
  game: string;
  player: string;
  grants: Record<string, unknown>;
  expires: string | null;
}

async function fetchGrants(gameId: string): Promise<GrantPayload | null> {
  try {
    const res = await fetch(`/api/grants?game=${encodeURIComponent(gameId)}`, { credentials: "include" });
    // 404 (nothing assigned) or 401 (anonymous) — fall back to declared defaults per T3.
    if (!res.ok) return null;
    const body = (await res.json()) as GrantResponse;
    return { grants: body.grants, expires: body.expires };
  } catch {
    return null; // network failure — same fallback
  }
}

function useSession(): Session {
  const [session, setSession] = useState<Session>({ status: "loading" });

  const probe = useCallback(() => {
    fetch("/api/profile", { credentials: "include" })
      .then((res) => (res.ok ? (res.json() as Promise<{ email: string }>) : null))
      .then((user) => setSession(user ? { status: "authenticated", email: user.email } : { status: "anonymous" }))
      .catch(() => setSession({ status: "anonymous" }));
  }, []);

  useEffect(() => {
    probe();
    sessionListeners.add(probe);
    return () => {
      sessionListeners.delete(probe);
    };
  }, [probe]);

  return session;
}

// Known detail codes from routes_auth.py's login route and fastapi-users'
// register router, resolved to a message a form can show directly. Anything
// unrecognized falls through to the raw detail string.
const KNOWN_ERROR_MESSAGES: Record<string, string> = {
  LOGIN_BAD_CREDENTIALS: "Incorrect email or password.",
  LOGIN_USER_NOT_VERIFIED: "Please verify your email before logging in — check your inbox.",
  REGISTER_USER_ALREADY_EXISTS: "An account with that email already exists.",
  RESEND_COOLDOWN_ACTIVE: "Please wait before requesting another verification email.",
  RESEND_DISABLED_CONTACT_SUPPORT: "Too many attempts — please contact support.",
  VERIFY_USER_BAD_TOKEN: "This verification link is invalid or has expired.",
  VERIFY_USER_ALREADY_VERIFIED: "This account is already verified — you can log in.",
  RESET_PASSWORD_BAD_TOKEN: "This reset link is invalid or has expired — request a new one.",
};

async function parseAuthError(res: Response): Promise<AuthError> {
  const body = await res.json().catch(() => null);
  const detail = (body as { detail?: unknown } | null)?.detail;

  // Suspended-account payload (req 1.4): { code: "suspended", reason, support_link }.
  if (detail && typeof detail === "object" && "code" in detail) {
    const d = detail as { code: string; reason?: string; support_link?: string };
    const message = d.reason ?? "Account suspended.";
    return d.support_link
      ? { code: d.code, message, supportLink: d.support_link }
      : { code: d.code, message };
  }
  if (typeof detail === "string") {
    return { code: detail, message: KNOWN_ERROR_MESSAGES[detail] ?? detail };
  }
  // Pydantic 422 validation errors: { detail: [{ msg, loc, ... }] } — e.g. the
  // under-13 date_of_birth rejection.
  if (Array.isArray(detail) && detail.length > 0 && typeof detail[0]?.msg === "string") {
    return { message: String(detail[0].msg).replace(/^Value error,\s*/, "") };
  }
  return { message: "Something went wrong. Please try again." };
}

async function login(email: string, password: string): Promise<AuthResult> {
  const res = await fetch("/api/auth/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({ username: email, password }),
  });
  if (!res.ok) return { ok: false, error: await parseAuthError(res) };
  notifySessionChanged();
  return { ok: true };
}

async function register(email: string, password: string, dateOfBirth: string): Promise<AuthResult> {
  const res = await fetch("/api/auth/register", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password, date_of_birth: dateOfBirth }),
  });
  if (!res.ok) return { ok: false, error: await parseAuthError(res) };
  // Registration alone doesn't log the user in — is_verified is false until
  // they click the emailed verification link (routes_auth.py's login route
  // rejects unverified accounts with LOGIN_USER_NOT_VERIFIED).
  return { ok: true };
}

async function logout(): Promise<void> {
  await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  notifySessionChanged();
}

async function resetPassword(token: string, newPassword: string): Promise<AuthResult> {
  const res = await fetch("/api/auth/reset-password", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password: newPassword }),
  });
  if (!res.ok) return { ok: false, error: await parseAuthError(res) };
  return { ok: true };
}

async function verifyEmail(token: string): Promise<AuthResult> {
  const res = await fetch("/api/auth/verify", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) return { ok: false, error: await parseAuthError(res) };
  return { ok: true };
}

async function forgotPassword(email: string): Promise<void> {
  await fetch("/api/auth/forgot-password", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

function connectHeartbeat(gameId: string, onSessionInvalid: () => void): () => void {
  return openHeartbeatSocket(gameId, () => {
    // The server only sends session_invalid when the session was actually
    // revoked — re-probe immediately so useSession()/useCatalogue() reflect
    // logged-out right away, rather than waiting for some other trigger.
    notifySessionChanged();
    onSessionInvalid();
  });
}

export const serverPlatform: PlatformAdapter = {
  useCatalogue,
  loadGame,
  createSaveStorage,
  useSession,
  login,
  register,
  logout,
  resetPassword,
  verifyEmail,
  forgotPassword,
  achievementSink,
  fetchGrants,
  connectHeartbeat,
  fetchPrelude,
};
